import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input } from '@/components/ui';
import { requestPasswordReset } from '../api/authApi';

export function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const result = await requestPasswordReset({ email });

    setIsLoading(false);

    if (result.error) {
      setError(result.error.message);
      return;
    }

    setIsSuccess(true);
  };

  if (isSuccess) {
    return (
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-secondary-900">Check your email</h3>
        <p className="text-secondary-600 text-sm">
          We've sent a password reset link to{' '}
          <span className="font-medium">{email}</span>
        </p>
        <p className="text-secondary-500 text-xs">
          Didn't receive the email? Check your spam folder or{' '}
          <button
            onClick={() => {
              setIsSuccess(false);
              setEmail('');
            }}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            try again
          </button>
        </p>
        <Link
          to="/login"
          className="inline-block mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          ← Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
          role="alert"
        >
          {error}
        </div>
      )}

      <p className="text-sm text-secondary-600">
        Enter your email address and we'll send you a link to reset your password.
      </p>

      <Input
        label="Email Address"
        type="email"
        name="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (error) setError(null);
        }}
        required
        autoComplete="email"
        disabled={isLoading}
      />

      <Button
        type="submit"
        className="w-full"
        size="lg"
        isLoading={isLoading}
      >
        Send Reset Link
      </Button>

      <div className="text-center">
        <Link
          to="/login"
          className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          ← Back to sign in
        </Link>
      </div>
    </form>
  );
}